import PropTypes from 'prop-types';
import React, { Component } from 'react';
import throttle from 'lodash.throttle';
import verge from 'verge';

import utils from '../../../lib/utils';

const inlineSrc =
  'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
const refs = [];
const elements = {};
let isBound = false;

const checkElementsInViewport = throttle(() => {
  refs.forEach((id) => {
    const options = elements[id];
    if (
      typeof options !== 'undefined' &&
      verge.inViewport(options.element, options.cushion)
    ) {
      if (options.callback) options.callback();
      checkUnbind(); // eslint-disable-line no-use-before-define
    }
  });
}, 150);

function checkCarouselEvent(evt) {
  if (
    evt.target.closest('.flickity-button') ||
    evt.target.closest('.flickity-page-dots')
  ) {
    setTimeout(checkElementsInViewport, 300);
  }
}

function checkBind() {
  if (!isBound && refs.length === 0 && window.addEventListener) {
    window.addEventListener('resize', checkElementsInViewport, false);
    window.addEventListener('scroll', checkElementsInViewport, false);
    window.addEventListener('touchend', checkElementsInViewport, false);
    window.addEventListener('click', checkCarouselEvent, true);
    isBound = true;
  }
}

function checkUnbind() {
  if (isBound && refs.length === 0 && window.removeEventListener) {
    window.removeEventListener('resize', checkElementsInViewport, false);
    window.removeEventListener('scroll', checkElementsInViewport, false);
    window.removeEventListener('touchend', checkElementsInViewport, false);
    window.removeEventListener('click', checkCarouselEvent, true);
    isBound = false;
  }
}

function addElement(id, options) {
  if (verge.inViewport(options.element, options.cushion)) {
    options.callback();
    return;
  }
  checkBind();
  refs.push(id);
  elements[id] = options;
}

function removeElement(id) {
  const idx = refs.indexOf(id);
  if (idx !== -1) {
    refs.splice(idx, 1);
    delete elements[id];
  }
}

const getId = (function makeIncrement(id) {
  return function increment() {
    id += 1;
    return id;
  };
})(-1);

(function printListener() {
  if (typeof window === 'undefined') return;

  let loadAllTries = 0;
  const loadAllClass = 'load-all-pending';
  const maxLoadAllTries = 10;
  // If all images have loaded or if `maxLoadAllTries` reached, invoke callback
  function onceAllLoaded(callback) {
    if (loadAllTries === maxLoadAllTries || refs.length === 0) {
      document.body.classList.remove(loadAllClass);
      if (typeof callback === 'function') callback();
    } else {
      loadAllTries += 1;
      if (loadAllTries <= maxLoadAllTries) {
        setTimeout(() => onceAllLoaded(callback), 250);
      }
    }
  }

  // on cmd+p, load all images and trigger print
  function printListener(evt) {
    if (evt.key === 'p' && evt.metaKey) {
      evt.preventDefault();
      window.loadAllLazyImages(utils.print);
    }
  }

  // trigger the loading callback for all hidden images
  window.loadAllLazyImages = function loadAllLazyImages(callback) {
    dry.events.publish('images:load');
    refs.forEach(
      (id) => typeof elements[id] !== 'undefined' && elements[id].callback(),
    );
    if (typeof callback === 'function') {
      document.body.classList.add(loadAllClass);
      onceAllLoaded(callback);
    }
  };
  // listen for ctrl+P - we want all images to be visible when page is printed
  document.body.addEventListener('keydown', printListener, false);
  // if url params start with print, load all images and print
  if (document.location.href.includes('?print=')) {
    setTimeout(() => window.loadAllLazyImages(utils.print), 500);
  }
})();

class Image extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
    };
    this.handleLoad = this.handleLoad.bind(this);
    this.id = getId();
  }

  componentDidMount() {
    const { cushion = 100, lazy = true } = this.props;
    if (lazy) {
      this.options = {
        callback: this.handleLoad,
        cushion,
        element: this.node,
      };
      // If the image is visible, load it by setting state
      // otherwise, add it to the list of element being watched
      // NOTE: setTimeout pops this action to the end of the call stack
      setTimeout(() => {
        if (verge.inViewport(this.node, cushion)) {
          this.setState({ loaded: true });
          checkBind();
        } else {
          addElement(this.id, this.options);
        }
      }, 0);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { src } = this.props;
    const { loaded } = this.state;
    return nextProps.src !== src || nextState.loaded !== loaded;
  }

  /**
   * Component updates when src changes as a result of grid/list toggle.
   * In this case, we should reset the lazy loading when necessary.
   */
  componentDidUpdate(prevProps) {
    const { lazy, src } = this.props;
    if (lazy && prevProps.src !== src) {
      setTimeout(() => {
        removeElement(this.id);
        addElement(this.id, this.options);
      }, 0);
    }
  }

  componentWillUnmount() {
    const { lazy } = this.props;
    if (lazy) {
      removeElement(this.id);
    }
  }

  handleLoad() {
    const { src } = this.props;
    if (src && elements[this.id]) {
      this.setState({ loaded: true });
    }
  }

  render() {
    const {
      alt = '',
      aspectRatio,
      className,
      lazy = true,
      src,
      ...restProps
    } = this.props;
    const { loaded } = this.state;
    return (
      <img
        crossOrigin="anonymous"
        data-aspect-ratio={aspectRatio}
        data-loaded={loaded}
        className={[lazy ? 'img-lazy' : '', 'img', className].join(' ')}
        alt={alt}
        onLoad={lazy && loaded ? () => removeElement(this.id) : null}
        ref={(node) => {
          this.node = node;
        }}
        src={lazy && !loaded ? inlineSrc : src}
        {...restProps}
      />
    );
  }
}

Image.propTypes = {
  /** Alt text for the image (Optional) */
  alt: PropTypes.string,

  /** If specified, cloudinary will be cut at the specified ratio */
  aspectRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** css classname for image */
  className: PropTypes.string,

  /**
   * The vertical threshold (pixels) at which the image
   * will load when a user is scrolling down OR up on the page.
   * i.e. if the image is withing `cushion` pixels of being visible, it will load
   */
  cushion: PropTypes.number,

  /** Whether or not to use lazy loading */
  lazy: PropTypes.bool,

  /** `src` property for the <img /> tag */
  src: PropTypes.string.isRequired,
};

export default Image;
